/**
 * Reducer — every synchronous state mutation from the source component,
 * expressed as pure cases. Async flows (timers) live in the action hook and
 * dispatch these cases at each tick. Mirrors the class methods 1:1.
 */
import type { State, Toast } from './state';
import type { AuditEntry, Version } from '../domain/types';
import { versionsFor, bumpRevList, publishList, selectedVersionId } from '../domain/versions';
import { allInts } from '../domain/catalog';
import { enabledMethods, recommendedMethod, sortMethods, METHOD_LABEL } from '../domain/auth';
import { unresolvedCount } from '../domain/mapping';

export type Action =
  | { type: 'PATCH'; patch: Partial<State> }
  | { type: 'TOAST'; msg: string; tone?: string }
  | { type: 'DISMISS_TOAST'; id: number }
  | { type: 'RECORD'; id: string; msg: string; kind?: string; time: string }
  | { type: 'BUMP_REV'; id: string }
  | { type: 'SET_SURFACE'; surface: State['surface'] }
  | { type: 'SET_ROLE'; role: State['role'] }
  | { type: 'SWITCH_ADAPTER'; id: string }
  | { type: 'SET_VERSION'; id: string; versionId: string }
  | { type: 'TOGGLE_GRANT'; intId: string; product: string; dataType: string; on: boolean }
  | { type: 'CONFIRM_REPLACE' }
  | { type: 'TOGGLE_AUTH_METHOD'; id: string; method: 'oauth' | 'apikey' | 'bearer'; time: string }
  | { type: 'SET_RECOMMENDED'; id: string; method: 'oauth' | 'apikey' | 'bearer' }
  | { type: 'AUTH_SET'; id: string; patch: Record<string, unknown> }
  | { type: 'WEBHOOK_SET'; id: string; patch: Record<string, unknown> }
  | { type: 'TOGGLE_DRAFT_EVENT'; ev: string }
  | { type: 'ADD_OUTBOUND'; id: string; hook: State['outboundHooks'][string][number]; time: string }
  | { type: 'TOGGLE_OUTBOUND'; id: string; hid: string }
  | { type: 'REMOVE_OUTBOUND'; id: string; hid: string; time: string }
  | { type: 'APPROVE'; time: string }
  | { type: 'CONN_SET'; id: string; conn: State['conn'][string] }
  | { type: 'PICK_TARGET'; fid: string; disc?: string; label: string }
  | { type: 'RESET_FIELD'; fid: string; disc?: string }
  | { type: 'TOGGLE_REMEMBER'; disc: 'subject' | 'name' }
  | { type: 'TOGGLE_FIELD'; fid: string }
  | { type: 'SAVE_META'; id: string; over: Record<string, unknown>; time: string }
  | { type: 'SAVE_MCP'; id: string; over: { url?: string; transport?: 'sse' | 'stdio'; extraTool?: boolean }; category: string; extraTool: boolean; time: string }
  | { type: 'DISCONNECT_MCP'; id: string }
  | { type: 'ADD_VERSION'; id: string; vstr: string };

const auditEntry = (s: State, msg: string, kind: string, time: string): AuditEntry => ({
  time,
  msg,
  kind: kind || 'config',
  actor: s.currentUser.name,
});

export function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'PATCH':
      return { ...s, ...a.patch };

    case 'TOAST':
      return { ...s, _tid: s._tid + 1, toasts: [...s.toasts, { id: s._tid + 1, msg: a.msg, tone: a.tone || 'info' }] };

    case 'DISMISS_TOAST':
      return { ...s, toasts: s.toasts.filter(t => t.id !== a.id) };

    case 'RECORD':
      return {
        ...s,
        audit: { ...s.audit, [a.id]: [auditEntry(s, a.msg, a.kind || 'config', a.time), ...(s.audit[a.id] || [])] },
      };

    case 'BUMP_REV': {
      const vs = bumpRevList(versionsFor(a.id, s.versions, s.adapters));
      return { ...s, versions: { ...s.versions, [a.id]: vs } };
    }

    case 'SET_SURFACE':
      return { ...s, surface: a.surface, selectedNode: null };

    case 'SET_ROLE':
      return {
        ...s,
        role: a.role,
        // Leaving the Softknack role closes every internal surface (builder, ops).
        surface: a.role !== 'softknack' && s.surface !== 'directory' ? 'directory' : s.surface,
        ingest: null,
      };

    case 'SWITCH_ADAPTER':
      return {
        ...s,
        activeAdapterId: a.id,
        selectedNode: null,
        expandedField: null,
        resolutions: { subject: null, name: null },
        approved: false,
      };

    case 'SET_VERSION':
      return { ...s, versionSel: { ...s.versionSel, [a.id]: a.versionId }, selectedNode: null };

    case 'TOGGLE_GRANT': {
      const key = a.product + ':' + a.dataType;
      if (a.on) {
        const r = { ...s.routes };
        delete r[key];
        return { ...s, routes: r };
      }
      const existing = s.routes[key];
      if (existing && existing !== a.intId) {
        return { ...s, replace: { product: a.product, dataType: a.dataType, fromId: existing, toId: a.intId } };
      }
      return { ...s, routes: { ...s.routes, [key]: a.intId } };
    }

    case 'CONFIRM_REPLACE': {
      if (!s.replace) return s;
      const key = s.replace.product + ':' + s.replace.dataType;
      return { ...s, routes: { ...s.routes, [key]: s.replace.toId }, replace: null };
    }

    case 'TOGGLE_AUTH_METHOD': {
      const ints = allInts(s.adapters, s.metaOverrides);
      const int = ints.find(i => i.id === a.id);
      const cur = enabledMethods(s.authCfg[a.id], int).slice();
      const has = cur.includes(a.method);
      let next = has ? cur.filter(x => x !== a.method) : [...cur, a.method];
      if (next.length === 0) return s; // guard handled in hook (toast)
      next = sortMethods(next);
      const cfg = { ...(s.authCfg[a.id] || {}), methods: next };
      const verb = (has ? 'Disabled ' : 'Enabled ') + METHOD_LABEL[a.method] + ' for tenants';
      return {
        ...s,
        authCfg: { ...s.authCfg, [a.id]: cfg },
        audit: { ...s.audit, [a.id]: [auditEntry(s, verb, 'config', a.time), ...(s.audit[a.id] || [])] },
      };
    }

    case 'SET_RECOMMENDED':
      return { ...s, authCfg: { ...s.authCfg, [a.id]: { ...(s.authCfg[a.id] || {}), recommended: a.method } } };

    case 'AUTH_SET':
      return { ...s, authCfg: { ...s.authCfg, [a.id]: { ...(s.authCfg[a.id] || {}), ...a.patch } } };

    case 'WEBHOOK_SET':
      return { ...s, webhookCfg: { ...s.webhookCfg, [a.id]: { ...(s.webhookCfg[a.id] || {}), ...a.patch } } };

    case 'TOGGLE_DRAFT_EVENT': {
      const has = s.outDraftEvents.includes(a.ev);
      return { ...s, outDraftEvents: has ? s.outDraftEvents.filter(e => e !== a.ev) : [...s.outDraftEvents, a.ev] };
    }

    case 'ADD_OUTBOUND': {
      const vs = bumpRevList(versionsFor(a.id, s.versions, s.adapters));
      return {
        ...s,
        outboundHooks: { ...s.outboundHooks, [a.id]: [...(s.outboundHooks[a.id] || []), a.hook] },
        outDraftUrl: '',
        outDraftEvents: ['created', 'updated'],
        audit: { ...s.audit, [a.id]: [auditEntry(s, 'Outbound webhook added → ' + a.hook.url, 'config', a.time), ...(s.audit[a.id] || [])] },
        versions: { ...s.versions, [a.id]: vs },
      };
    }

    case 'TOGGLE_OUTBOUND':
      return {
        ...s,
        outboundHooks: {
          ...s.outboundHooks,
          [a.id]: (s.outboundHooks[a.id] || []).map(h => (h.id === a.hid ? { ...h, active: !h.active } : h)),
        },
      };

    case 'REMOVE_OUTBOUND':
      return {
        ...s,
        outboundHooks: { ...s.outboundHooks, [a.id]: (s.outboundHooks[a.id] || []).filter(h => h.id !== a.hid) },
        audit: { ...s.audit, [a.id]: [auditEntry(s, 'Outbound webhook removed', 'config', a.time), ...(s.audit[a.id] || [])] },
      };

    case 'APPROVE': {
      // activeAdapter id
      const id = s.activeAdapterId;
      const selId = selectedVersionId(id, s.versions, s.adapters, s.versionSel);
      const curVs = versionsFor(id, s.versions, s.adapters);
      const selVendor = (curVs.find(v => v.id === selId) || ({} as Version)).vendor;
      const vs = publishList(curVs, selId);
      const newRev = ((curVs.find(v => v.id === selId) || ({} as Version)).rev || 0) + 1;
      const msg = 'Approved & published v1 × ' + selVendor + ' · rev ' + newRev;
      return {
        ...s,
        approved: true,
        approveOpen: false,
        versions: { ...s.versions, [id]: vs },
        adapters: s.adapters.map(x => (x.id === id ? { ...x, inDirectory: true, approved: true } : x)),
        audit: { ...s.audit, [id]: [auditEntry(s, msg, 'publish', a.time), ...(s.audit[id] || [])] },
      };
    }

    case 'CONN_SET':
      return { ...s, conn: { ...s.conn, [a.id]: a.conn } };

    case 'PICK_TARGET': {
      const next: Partial<State> = { overrides: { ...s.overrides, [a.fid]: a.label }, expandedField: null, fieldSearch: '' };
      if (a.disc) next.resolutions = { ...s.resolutions, [a.disc]: a.label } as State['resolutions'];
      return { ...s, ...next };
    }

    case 'RESET_FIELD': {
      const ov = { ...s.overrides };
      delete ov[a.fid];
      const next: Partial<State> = { overrides: ov, expandedField: null };
      if (a.disc) next.resolutions = { ...s.resolutions, [a.disc]: null } as State['resolutions'];
      return { ...s, ...next };
    }

    case 'TOGGLE_REMEMBER':
      return { ...s, remember: { ...s.remember, [a.disc]: !s.remember[a.disc] } };

    case 'TOGGLE_FIELD':
      return { ...s, expandedField: s.expandedField === a.fid ? null : a.fid, fieldSearch: '' };

    case 'SAVE_META': {
      const vs = bumpRevList(versionsFor(a.id, s.versions, s.adapters));
      return {
        ...s,
        metaOverrides: { ...s.metaOverrides, [a.id]: a.over },
        metaManage: null,
        detailId: a.id,
        versions: { ...s.versions, [a.id]: vs },
        audit: { ...s.audit, [a.id]: [auditEntry(s, 'Connector details edited', 'config', a.time), ...(s.audit[a.id] || [])] },
      };
    }

    case 'SAVE_MCP': {
      const vs = bumpRevList(versionsFor(a.id, s.versions, s.adapters));
      return {
        ...s,
        adapters: s.adapters.map(ad =>
          ad.id === a.id ? { ...ad, category: a.category, _transport: a.over.transport, _mcpUrl: a.over.url, _extraTool: a.over.extraTool } : ad,
        ),
        metaOverrides: { ...s.metaOverrides, [a.id]: { ...(s.metaOverrides[a.id] || {}), category: a.category } },
        mcpOverrides: { ...s.mcpOverrides, [a.id]: a.over },
        mcpManage: null,
        detailId: a.id,
        versions: { ...s.versions, [a.id]: vs },
        audit: {
          ...s.audit,
          [a.id]: [auditEntry(s, 'MCP connection updated' + (a.extraTool ? ' · tools re-synced' : ''), 'config', a.time), ...(s.audit[a.id] || [])],
        },
      };
    }

    case 'DISCONNECT_MCP':
      return { ...s, adapters: s.adapters.filter(ad => ad.id !== a.id), mcpManage: null };

    case 'ADD_VERSION': {
      const vs = versionsFor(a.id, s.versions, s.adapters);
      const newId = a.id + '|' + a.vstr + '|' + Date.now();
      const next: Version[] = [
        { id: newId, catalog: 'v1', vendor: a.vstr, status: 'draft', tag: null, rev: 0, execs: 0 },
        ...vs.map(v => (v.status === 'published' ? { ...v, tag: 'N-1' } : { ...v, tag: null })),
      ];
      return {
        ...s,
        versions: { ...s.versions, [a.id]: next },
        versionSel: { ...s.versionSel, [a.id]: newId },
        activeAdapterId: a.id,
        surface: 'builder',
        direction: 'outbound',
        rightTab: 'auth',
        resolutions: { subject: null, name: null },
        selectedNode: null,
        ingest: null,
      };
    }

    default:
      return s;
  }
}

/** Helper re-exported for the hook (unresolved count for approve guard). */
export { unresolvedCount };
